import { warehouseClient, extractStatusCode } from "@/infrastructure/http/warehouseClient";
import type {
  CategoryListResponse,
  CreateCategoryInput,
  UpdateCategoryInput,
  UpdateCategoryStatusInput,
  CategoryErrorCode,
  ApiEnvelope,
} from "@/domain/types";

export function createCategoryError(code: CategoryErrorCode): Error {
  const err = new Error(code);
  err.name = "CategoryError";
  return err;
}

export function isCategoryError(err: unknown): err is Error & { message: CategoryErrorCode } {
  return err instanceof Error && err.name === "CategoryError";
}

function mapError(statusCode: number): CategoryErrorCode {
  if (statusCode === 409) return "DUPLICATE_CODE";
  if (statusCode === 400) return "VALIDATION";
  if (statusCode === 404) return "NOT_FOUND";
  if (statusCode === 403) return "FORBIDDEN";
  if (statusCode === 401) return "UNAUTHORIZED";
  return "UNKNOWN";
}

export async function listCategories(params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}): Promise<CategoryListResponse> {
  const query = new URLSearchParams();
  if (params?.page) query.set("page", String(params.page));
  if (params?.limit) query.set("limit", String(params.limit));
  if (params?.search) query.set("search", params.search);
  if (params?.status) query.set("status", params.status);

  const response = await warehouseClient.get<ApiEnvelope<CategoryListResponse>>(
    `/category?${query.toString()}`
  );
  return response.data.data;
}

export async function createCategory(input: CreateCategoryInput): Promise<void> {
  try {
    await warehouseClient.post("/category", input);
  } catch (err) {
    throw createCategoryError(mapError(extractStatusCode(err)));
  }
}

export async function updateCategory(input: UpdateCategoryInput): Promise<void> {
  try {
    await warehouseClient.patch("/category", input);
  } catch (err) {
    throw createCategoryError(mapError(extractStatusCode(err)));
  }
}

export async function updateCategoryStatus(input: UpdateCategoryStatusInput): Promise<void> {
  try {
    await warehouseClient.patch("/category/status", input);
  } catch (err) {
    throw createCategoryError(mapError(extractStatusCode(err)));
  }
}
